import styled, { css } from 'styled-components'
import get from 'lodash/get'

import Loader from 'src/comps/Atom/Loader'
import { ButtonProps } from 'src/comps/Atom/Buttons/Button'

import { AtomButtonSize } from 'src/comps/Atom/types/atom.d'

export const extraSmallSize = css`
  font-size: ${props => props.theme.fonts.sizes.s};
  padding-block: 3px;
  padding-inline: 8px;
`

export const smallSize = css`
  font-size: ${props => props.theme.fonts.sizes.s};
  line-height: 143%;
  padding-block: 4px;
  padding-inline: 10px;
`

export const mediumSize = css`
  font-size: ${props => props.theme.fonts.sizes.m};
  line-height: 150%;
  padding-block: 10px;
  padding-inline: 18px;
`

const fullWidth = css`
  width: 100%;
`

const getSize = (size: AtomButtonSize) => {
  switch (size) {
    case 'xs':
      return extraSmallSize
    case 's':
      return smallSize
    case 'm':
      return mediumSize
    default:
      return mediumSize
  }
}

export const ButtonBase = styled.button.attrs<ButtonProps>(props => ({
  color: props.color ? get(props.theme, `colors.${props.color}`) : props.theme.colors.grey8,
  backgroundColor: props.backgroundColor
    ? get(props.theme, `colors.${props.backgroundColor}`)
    : props.theme.colors.grey0,
  srgbBackgroundColor: props.backgroundColor
    ? get(props.theme, `colors.srgb.${props.backgroundColor}`)
    : props.theme.colors.srgb.grey0,
  borderColor: props.borderColor
    ? get(props.theme, `colors.${props.borderColor}`)
    : props.theme.colors.grey0,
  srgbBorderColor: props.borderColor
    ? get(props.theme, `colors.srgb.${props.borderColor}`)
    : props.theme.colors.srgb.grey0,
}))<ButtonProps>`
  appearance: none;
  border: none;
  cursor: ${props => (props.isLoading ? 'not-allowed' : 'pointer')};
  pointer-events: ${props => (props.isLoading ? 'none' : 'auto')};
  font-family: ${props => props.theme.fonts.families.primary};
  font-weight: 700;
  position: relative;
  text-decoration: none;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;

  color: ${props => {
    if (props.outlined || props.filledLight) return 'var(--color--neutral--800)'
    return props.color
  }};
  background-color: ${props => {
    if (props.outlined) return 'var(--color--base--white)'
    if (props.filledLight) return 'var(--color--primary--50)'
    return props.backgroundColor
  }};
  border: ${props => {
    if (props.outlined) return '2px solid var(--color--neutral--400)'
    if (props.filledLight) return 'none'
    return `2px solid ${props.borderColor}`
  }};
  border-radius: ${props => (props.rounded ? 'var(--radius-500)' : 'var(--radius-8)')};

  ${props => getSize(props.size)}

  ${props => props.fullWidth && fullWidth}

  svg {
    fill: ${props => {
      if (props.outlined || props.filledLight) return 'var(--color--neutral--800)'
      return props.color
    }};
  }

  :active {
    outline: none;
    text-decoration: none;
    color: ${props => {
      if (props.outlined || props.filledLight) return 'var(--color--neutral--800)'
      return props.color
    }};
    background-color: ${props => {
      if (props.outlined) return 'var(--color--state--hover-on-light)'
      if (props.filledLight)
        return 'color-mix(in srgb, var(--color--primary--50), var(--color--state--hover-on-primary))'
      return `color(
            srgb calc((( ${props.srgbBackgroundColor?.r} * 80) / 255) * 1%)
              calc((( ${props.srgbBackgroundColor?.g} * 80) / 255) * 1%)
              calc((( ${props.srgbBackgroundColor?.b} * 80) / 255) * 1%)
        )`
    }};
    border: ${props => {
      if (props.outlined) return '2px solid var(--color--neutral--400)'
      if (props.filledLight) return 'none'
      return `2px solid color(
        srgb calc(((${props.srgbBorderColor?.r} * 80) / 255) * 1%)
          calc(((${props.srgbBorderColor?.g} * 80) / 255) * 1%)
          calc(((${props.srgbBorderColor?.b} * 80) / 255) * 1%)
              )`
    }};
  }

  @media (pointer: fine) {
    &:hover {
      outline: none;
      text-decoration: none;
      color: ${props => {
        if (props.outlined || props.filledLight) return 'var(--color--neutral--800)'
        return props.color
      }};
      background-color: ${props => {
        if (props.outlined) return 'var(--color--state--hover-on-light)'
        if (props.filledLight)
          return 'color-mix(in srgb, var(--color--primary--50), var(--color--state--hover-on-primary))'
        return `color(
            srgb calc((( ${props.srgbBackgroundColor?.r} * 80) / 255) * 1%)
              calc((( ${props.srgbBackgroundColor?.g} * 80) / 255) * 1%)
              calc((( ${props.srgbBackgroundColor?.b} * 80) / 255) * 1%)
        )`
      }};
      border: ${props => {
        if (props.outlined) return '2px solid var(--color--neutral--400)'
        if (props.filledLight) return 'none'
        return `2px solid color(
        srgb calc(((${props.srgbBorderColor?.r} * 80) / 255) * 1%)
          calc(((${props.srgbBorderColor?.g} * 80) / 255) * 1%)
          calc(((${props.srgbBorderColor?.b} * 80) / 255) * 1%)
              )`
      }};
    }
  }

  ${props =>
    props.isLoading &&
    css`
      background-color: color(
        srgb calc(((${props.srgbBackgroundColor?.r} * 80) / 255) * 1%)
          calc(((${props.srgbBackgroundColor?.g} * 80) / 255) * 1%)
          calc(((${props.srgbBackgroundColor?.b} * 80) / 255) * 1%)
      );
      border: 2px solid
        color(
          srgb calc(((${props.srgbBorderColor?.r} * 80) / 255) * 1%)
            calc(((${props.srgbBorderColor?.g} * 80) / 255) * 1%)
            calc(((${props.srgbBorderColor?.b} * 80) / 255) * 1%)
        );
    `}

  ${props =>
    props.isLoading &&
    props.loaderPosition === 'content' &&
    css`
      color: transparent;

      &:active {
        color: transparent;
      }

      @media (pointer: fine) {
        &:hover {
          color: transparent;
        }
      }

      svg {
        fill: transparent;
      }
    `}

  &[disabled] {
    background-color: ${props => props.theme.colors.grey5};
    border-color: ${props => props.theme.colors.grey5};
    color: ${props => props.theme.colors.grey4};
    cursor: 'not-allowed';
    pointer-events: none;

    @media (pointer: fine) {
      &:hover {
        background-color: inherit;
        opacity: 1;
      }
    }

    svg {
      fill: ${props => props.theme.colors.grey4};
    }
  }
`

export const SvgIcon = styled.span.attrs<{ size: AtomButtonSize }>(() => ({
  size: '20px',
}))<{ size: AtomButtonSize }>`
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: ${props => props.size};
    height: ${props => props.size};
  }
`

export const ButtonLoader = styled(Loader).attrs(props => ({
  color: props.color ? get(props.theme, `colors.${props.color}`) : props.theme.colors.grey8,
}))`
  position: absolute;
  margin-inline: auto;
  margin-block: auto;
  inset-block-start: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;
  text-align: center;
`
